// Tracklist parsing and validation for mix uploads.
// Parsing is intentionally loose: accepts Serato/Rekordbox/Virtual DJ-style
// exports, varied time formats, en/em-dashes, and time-at-start or -end.

export function parseTimeToSeconds(t) {
  const parts = t.split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return null;
}

export function formatDuration(sec) {
  if (!sec || isNaN(sec)) return '0:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Normalize en/em-dashes to a plain hyphen and trim.
function normalizeLine(raw) {
  return raw.replace(/\u2014/g, '-').replace(/\u2013/g, '-').trim();
}

// Try to extract {time, artist, title} from a single line, loosely. Finds the
// first time token anywhere; the rest of the line (after the time, or before
// it if nothing follows) is split on the first hyphen into artist/title.
export function parseTrackLine(line) {
  const l = normalizeLine(line);
  if (!l) return null;
  const timeMatch = l.match(/\b(\d{1,2}:\d{2}(?::\d{2})?)\b/);
  if (!timeMatch) return null;
  const time = timeMatch[1];
  const before = l.slice(0, timeMatch.index).trim();
  const after = l.slice(timeMatch.index + timeMatch[0].length).trim();
  const rest = after || before;
  const dashIdx = rest.indexOf('-');
  let artist = '';
  let title = '';
  if (dashIdx > 0) {
    artist = rest.slice(0, dashIdx).trim();
    title = rest.slice(dashIdx + 1).trim();
  } else {
    title = rest;
  }
  return { time, artist, title };
}

// Normalize a block of text into clean "0:00 Artist - Title" lines, dropping
// lines that can't be parsed.
export function normalizeTracklist(text) {
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out = [];
  for (const line of lines) {
    const p = parseTrackLine(line);
    if (!p) continue;
    if (parseTimeToSeconds(p.time) === null) continue;
    const label = p.artist && p.title ? `${p.artist} - ${p.title}` : p.title || p.artist || '';
    out.push(`${p.time} ${label}`.trim());
  }
  return out.join('\n');
}

// Soft validation — returns warning objects ({key, vars}) for the UI to
// translate. Empty array means no warnings. Tracklist is optional, so an
// empty tracklist yields no warnings and never blocks submission.
export function validateTracklist(text, durationSeconds) {
  const warnings = [];
  const lines = (text || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  let prevSec = null;
  let lastSec = null;
  lines.forEach((line, i) => {
    const p = parseTrackLine(line);
    if (!p) {
      warnings.push({ key: 'Line {n}: could not parse — skipped.', vars: { n: i + 1 } });
      return;
    }
    const sec = parseTimeToSeconds(p.time);
    if (sec === null) {
      warnings.push({ key: 'Line {n}: invalid time.', vars: { n: i + 1 } });
      return;
    }
    if (prevSec !== null && sec < prevSec) {
      warnings.push({ key: 'Line {n}: time goes backward.', vars: { n: i + 1 } });
    }
    prevSec = sec;
    lastSec = sec;
  });
  if (durationSeconds && lastSec !== null && lastSec >= durationSeconds) {
    warnings.push({
      key: 'Last track ({last}) is at or after the file duration ({dur}).',
      vars: { last: formatDuration(lastSec), dur: formatDuration(durationSeconds) },
    });
  }
  return warnings;
}